'use client';

/**
 * CALENDRIER HIJRI — conversion réelle via Intl (calendrier umalqura)
 * Navigation mois, jours spéciaux (Ramadan, Aïd, Achoura, Laylat al-Qadr…),
 * équivalence grégorienne, lecture vocale de la date.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { speakText } from '@/lib/speech';
import { CalendarDays, ChevronLeft, ChevronRight, Moon, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import PageBanner from './PageBanner';

const HIJRI_MONTHS = {
  fr: ['Mouharram', 'Safar', 'Rabi al-Awwal', 'Rabi ath-Thani', 'Joumada al-Oula', 'Joumada ath-Thania', 'Rajab', 'Chaabane', 'Ramadan', 'Chawwal', 'Dhou al-Qi\'da', 'Dhou al-Hijja'],
  en: ['Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani', 'Jumada al-Ula', 'Jumada al-Thania', 'Rajab', 'Shaban', 'Ramadan', 'Shawwal', 'Dhu al-Qi\'dah', 'Dhu al-Hijjah'],
  ar: ['محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني', 'جمادى الأولى', 'جمادى الآخرة', 'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'],
};
const GREG_MONTHS = {
  fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
};

// Jours spéciaux (mois hijri 1-12, jour)
const SPECIAL_DAYS: Record<string, { label: { fr: string; en: string; ar: string }; icon: string }> = {
  '1-1': { label: { fr: 'Nouvel An Hégirien', en: 'Hijri New Year', ar: 'رأس السنة الهجرية' }, icon: '🌙' },
  '1-10': { label: { fr: 'Achoura', en: 'Ashura', ar: 'عاشوراء' }, icon: '⭐' },
  '3-12': { label: { fr: 'Mawlid an-Nabi', en: 'Mawlid an-Nabi', ar: 'المولد النبوي' }, icon: '✨' },
  '7-27': { label: { fr: 'Al-Isra wal-Miraj', en: 'Al-Isra wal-Miraj', ar: 'الإسراء والمعراج' }, icon: '🌙' },
  '8-15': { label: { fr: 'Mi-Chaabane', en: 'Mid-Shaban', ar: 'ليلة النصف من شعبان' }, icon: '🌙' },
  '9-27': { label: { fr: 'Laylat al-Qadr (probable)', en: 'Laylat al-Qadr (likely)', ar: 'ليلة القدر (مرجحة)' }, icon: '🌟' },
  '10-1': { label: { fr: 'Aïd al-Fitr', en: 'Eid al-Fitr', ar: 'عيد الفطر' }, icon: '🎉' },
  '12-9': { label: { fr: 'Jour de Arafat', en: 'Day of Arafah', ar: 'يوم عرفة' }, icon: '⛰' },
  '12-10': { label: { fr: 'Aïd al-Adha', en: 'Eid al-Adha', ar: 'عيد الأضحى' }, icon: '🐏' },
};

function hijriToday(): { y: number; m: number; d: number } {
  const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { day: 'numeric', month: 'numeric', year: 'numeric' }).formatToParts(new Date());
  const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value.replace(/[^0-9]/g, '') || '1', 10);
  return { y: get('year'), m: get('month'), d: get('day') };
}

export default function HijriSection() {
  const { uiLang } = useApp();
  const lang = (uiLang === 'ar' ? 'ar' : uiLang === 'en' ? 'en' : 'fr') as 'fr' | 'en' | 'ar';
  const today = useMemo(() => hijriToday(), []);
  const [view, setView] = useState({ y: today.y, m: today.m });

  const lbl = (fr: string, en: string, ar: string) => (lang === 'ar' ? ar : lang === 'en' ? en : fr);

  // Grille du mois : premier jour + longueur du mois (umalqura)
  const grid = useMemo(() => {
    // clé numérique triable : Y*10000 + M*100 + D (évite toute comparaison lexicographique)
    const hijriKey = (ms: number) => {
      const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { day: 'numeric', month: 'numeric', year: 'numeric' }).formatToParts(new Date(ms));
      const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value.replace(/[^0-9]/g, '') || '0', 10);
      return get('year') * 10000 + get('month') * 100 + get('day');
    };
    const target = view.y * 10000 + view.m * 100 + 1;
    // bissection sur le timestamp grégorien
    let lo = Date.UTC(1900, 0, 1);
    let hi = Date.UTC(2150, 0, 1);
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (hijriKey(mid) >= target) hi = mid; else lo = mid;
    }
    const first = hi;
    const greg = new Date(first);
    const startOffset = greg.getUTCDay(); // 0 = dimanche → on aligne lundi = 0
    const mondayOffset = (startOffset + 6) % 7;

    // Longueur du mois : jour 1 du mois suivant − jour 1 de ce mois
    const nextM = view.m === 12 ? 1 : view.m + 1;
    const nextY = view.m === 12 ? view.y + 1 : view.y;
    const length = Math.round((hijriKeyFirst(nextY, nextM) - first) / 86400000);

    function hijriKeyFirst(hy: number, hm: number): number {
      let l = Date.UTC(1900, 0, 1);
      let h = Date.UTC(2150, 0, 1);
      const tg = hy * 10000 + hm * 100 + 1;
      while (h - l > 1) {
        const mid = Math.floor((l + h) / 2);
        if (hijriKey(mid) >= tg) h = mid; else l = mid;
      }
      return h;
    }

    const days: { hd: number; greg: Date }[] = [];
    for (let i = 0; i < length; i++) {
      const g = new Date(first + i * 86400000);
      days.push({ hd: i + 1, greg: g });
    }
    return { mondayOffset, days };
  }, [view]);

  const monthName = HIJRI_MONTHS[lang][view.m - 1];
  const gref = grid.days[0]?.greg;
  const gregLabel = gref ? `${GREG_MONTHS[lang][gref.getUTCMonth()]} ${gref.getUTCFullYear()}` : '';

  const isToday = (hd: number) => hd === today.d && view.m === today.m && view.y === today.y;
  const special = (hd: number) => SPECIAL_DAYS[`${view.m}-${hd}`];

  const monthArabic = ['محرم','صفر','ربيع الأول','ربيع الثاني','جمادى الأولى','جمادى الآخرة','رجب','شعبان','رمضان','شوال','ذو القعدة','ذو الحجة'][view.m - 1];

  const dayNames = lang === 'ar'
    ? ['اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت', 'أحد']
    : lang === 'en'
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <>
      <PageBanner
        src="/images/banners/hijri_banner.png"
        eyebrow="Hijri — الهِجْرِي"
        title={lbl('Calendrier Hégirien', 'Hijri Calendar', 'التقويم الهجري')}
        sub={`${lbl("Conversion astronomique Umm al-Qura — aujourd'hui :", 'Umm al-Qura astronomical conversion — today:', 'تحويل أم القرى الفلكي — اليوم:')} ${today.d} ${HIJRI_MONTHS[lang][today.m - 1]} ${today.y}`}
      />
      <section id="section-hijri" className="relative mx-auto max-w-4xl px-4 py-14 sm:px-6">

      <div className="mt-10 rounded-2xl border border-accent/25 bg-gradient-to-b from-emerald-950/70 to-black/30 p-6 sm:p-8">
        {/* En-tête du mois */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setView((v) => (v.m === 1 ? { y: v.y - 1, m: 12 } : { ...v, m: v.m - 1 }))}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-accent/30 text-foreground hover:bg-accent/10"
          >
            <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
          </button>
          <div className="text-center">
            <p className="font-display text-2xl font-bold text-foreground">
              {view.m} — {monthName} {view.y} <span dir="rtl" className="font-arabic text-accent">{monthArabic}</span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{gregLabel} · 1447 H ⇄ {new Date().getFullYear()}</p>
          </div>
          <button
            onClick={() => setView((v) => (v.m === 12 ? { y: v.y + 1, m: 1 } : { ...v, m: v.m + 1 }))}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-accent/30 text-foreground hover:bg-accent/10"
          >
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="mt-6 grid grid-cols-7 gap-1.5 text-center">
          {dayNames.map((d) => (
            <span key={d} className="py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d}</span>
          ))}
          {Array.from({ length: grid.mondayOffset }).map((_, i) => (
            <span key={`pad-${i}`} />
          ))}
          {grid.days.map(({ hd, greg }) => {
            const sp = special(hd);
            return (
              <button
                key={hd}
                onClick={() => sp && speakText(sp.label.ar, 'ar')}
                title={sp ? sp.label[lang] : `${greg.getUTCDate()} ${GREG_MONTHS[lang][greg.getUTCMonth()]}`}
                className={cn(
                  'relative aspect-square rounded-lg border p-1 text-sm transition-all',
                  isToday(hd)
                    ? 'border-accent bg-accent text-emerald-950 font-bold shadow-[0_0_15px_rgba(212,175,55,0.4)]'
                    : sp
                      ? 'border-accent/60 bg-accent/15 text-accent'
                      : 'border-accent/10 bg-black/20 text-foreground/80 hover:border-accent/40'
                )}
              >
                {hd}
                {sp && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[9px]">{sp.icon}</span>}
                {greg.getUTCDay() === 5 && !sp && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[9px] text-accent/60">🕌</span>}
              </button>
            );
          })}
        </div>

        {/* Événements du mois */}
        <div className="mt-6 space-y-2">
          {grid.days.filter(({ hd }) => special(hd)).map(({ hd }) => {
            const sp = special(hd)!;
            return (
              <div key={hd} className="flex items-center gap-3 rounded-xl border border-accent/20 bg-black/30 p-3">
                <span className="text-xl">{sp.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{sp.label[lang]}</p>
                  <p className="text-xs text-muted-foreground">{hd} {monthName} {view.y}</p>
                </div>
                <button onClick={() => speakText(sp.label.ar, 'ar')} className="rounded-full bg-accent/10 px-3 py-1.5 text-xs text-accent hover:bg-accent/25">
                  🔊
                </button>
              </div>
            );
          })}
          {!grid.days.some(({ hd }) => special(hd)) && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarDays className="h-4 w-4 text-accent/50" />
              {lbl('Aucun jour spécial ce mois', 'No special day this month', 'لا أيام خاصة في هذا الشهر')}
            </p>
          )}
        </div>
      </div>

      {/* Légende mois sacrés */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Moon className="h-3.5 w-3.5 text-accent" /> {lbl('Mois sacrés : 1, 7, 11, 12', 'Sacred months: 1, 7, 11, 12', 'الأشهر الحرم: ١، ٧، ١١، ١٢')}</span>
        <span className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-accent" /> {lbl('Ramadan = mois 9', 'Ramadan = month 9', 'رمضان = الشهر ٩')}</span>
      </div>
      </section>
    </>
  );
}
